import { ArrowUpRight, Lock } from "lucide-react";

/**
 * One app in the grid.
 *
 * Every card is a fixed width so rows line up into columns — the old layout let
 * cards size to their contents, which left ragged rows. The name gets two lines
 * rather than being cut off at a single truncated line, and the block is a fixed
 * height so cards stay aligned whether a name wraps or not.
 */
export default function AppCard({ app, isDragging, dragHandleProps, onOpen }) {
  return (
    <div
      role="button"
      tabIndex={0}
      title={app.description || app.url}
      className={`group relative flex w-[104px] flex-col items-center gap-2.5 rounded-2xl px-2 pt-4 pb-3
        cursor-pointer select-none outline-none
        transition duration-200 ease-out
        hover:bg-white/[0.07] hover:-translate-y-0.5
        focus-visible:bg-white/[0.07] focus-visible:ring-2 focus-visible:ring-white/40
        ${isDragging ? "bg-white/10 ring-1 ring-white/20 shadow-2xl shadow-black/40 rotate-1" : ""}`}
      onClick={() => onOpen(app)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(app);
        }
      }}
      {...dragHandleProps}
    >
      {/* The icon sits on nothing — no tile, no plate — scaled to fill a
          consistent box so a 48px favicon and a 512px logo carry equal weight. */}
      <div className="relative h-14 w-14 shrink-0">
        {app.icon_url ? (
          <img
            src={app.icon_url}
            alt=""
            draggable={false}
            className="h-full w-full object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]
              transition-transform duration-200 ease-out group-hover:scale-[1.08]"
          />
        ) : (
          // Only the iconless case gets a surface, so its initial stays legible.
          <div
            className="flex h-full w-full items-center justify-center rounded-2xl
              bg-white/10 ring-1 ring-inset ring-white/10 text-xl font-semibold text-white/70
              transition-transform duration-200 ease-out group-hover:scale-[1.08]"
          >
            {app.name.charAt(0).toUpperCase()}
          </div>
        )}

        {app.admin_only && (
          <span
            className="absolute -right-1 -top-1 rounded-full bg-slate-900/90 p-1 ring-1 ring-white/15"
            title="Admins only"
          >
            <Lock className="h-2.5 w-2.5 text-white/80" />
          </span>
        )}
      </div>

      {/* Two lines, fixed height: long names read in full and short ones still
          leave every card the same height. */}
      <span className="line-clamp-2 min-h-[2.5rem] text-center text-[13px] font-medium leading-tight text-white/85 transition-colors group-hover:text-white">
        {app.name}
      </span>

      <ArrowUpRight
        className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 text-white/0 transition-colors duration-200 group-hover:text-white/45"
        aria-hidden
      />
    </div>
  );
}
